import { Injectable } from '@angular/core'
import { Store } from '@ngxs/store'
import { QuoteMetaState } from 'src/store/quote/quote.meta.state'
import { ProductTypes } from '../smart-suggestion.model'
import { getIsCombinationSelected } from './smart-suggestion.helper'

/**
 * POLICY: BYD2-565
 * Description: The quantity of cable meters per panels
 * readonly needMetersPerPanel: number = 3
 */

// The types are 'any' because the initial object is of type 'any', and the entire store needs to be refactored
interface IMappedCables {
  id: string
  title: string
  description: string
  metersQuantity: number | null
  color: 'RED' | 'BLACK' | null
  amount: number
}

@Injectable({ providedIn: 'root' })
export class CablesHelper {
  readonly needMetersPerPanel: number = 3

  constructor(private store: Store) {}

  getCablesSuggestions() {
    const cables = this.store
      .selectSnapshot(QuoteMetaState.getCables)
      .map((cable) => ({
        id: cable.id,
        title: cable.title,
        description: cable.description,
        metersQuantity: cable.metersQuantity,
        color: cable.color,
        amount: 0,
      })) as IMappedCables[]

    const selectedPanels = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanels,
    )
    if (selectedPanels.length === 0) {
      return []
    }
    const panelsQuantity = this.calculatePanelsQuantity(selectedPanels)
    const panelsMetersQuantity = panelsQuantity * this.needMetersPerPanel

    console.log(
      '%cNEED CABLE METERS QUANTITY ↓',
      'font-weight: bold; color: white; background-color: green;',
    )
    console.log(panelsMetersQuantity)

    const redCables = cables.filter((cable) => cable.color === 'RED')
    const blackCables = cables.filter((cable) => cable.color === 'BLACK')
    const redCombination = this.getBestCableCombination({
      cables: redCables,
      metersRequired: panelsMetersQuantity,
    })
    const blackCombination = this.getBestCableCombination({
      cables: blackCables,
      metersRequired: panelsMetersQuantity,
    })
    const seggestions = this.parseOptions(redCombination, blackCombination)

    return seggestions
  }

  private getBestCableCombination({
    cables,
    metersRequired,
  }: {
    cables: IMappedCables[]
    metersRequired: number
  }) {
    const combination = []
    let remainMeters = this.getRoundedRequiredMeters({ cables, metersRequired })
    const descCables = [...cables].sort(
      (a, b) => b.metersQuantity - a.metersQuantity,
    )
    for (const cable of descCables) {
      if (remainMeters <= 0) {
        break
      }
      const multiplier = Math.floor(remainMeters / cable.metersQuantity)
      const cableAmoutMeters = multiplier * cable.metersQuantity
      if (multiplier > 0) {
        cable.amount = multiplier
        remainMeters -= cableAmoutMeters
        combination.push(cable)
      }
    }
    return combination
  }

  private parseOptions(
    redCombination: IMappedCables[],
    blackCombination: IMappedCables[],
  ) {
    const combinations = [...redCombination, ...blackCombination]
    const title = combinations
      .reduce((accumulator, cable) => {
        accumulator += `${cable.amount}x ${cable.title}, `
        return accumulator
      }, '')
      .slice(0, -2)
    const content = combinations.map((cable) => ({
      id: cable.id,
      quantity: cable.amount,
      type: ProductTypes.CABLE,
    }))
    const selectedCables = this.store.selectSnapshot(QuoteMetaState.getCables)

    return [
      {
        title,
        isSelected: getIsCombinationSelected({
          content,
          products: selectedCables,
        }),
        content,
      },
    ]
  }

  private calculatePanelsQuantity(panels: any[]): number {
    return panels.reduce((acc, curr) => acc + curr.quantity, 0)
  }

  private getRoundedRequiredMeters({
    cables,
    metersRequired,
  }: {
    cables: IMappedCables[]
    metersRequired: number
  }) {
    const smallestCable = [...cables].sort(
      (a, b) => a.metersQuantity - b.metersQuantity,
    )[0]
    return (
      Math.ceil(metersRequired / smallestCable?.metersQuantity) *
      smallestCable?.metersQuantity
    )
  }
}
